import * as React from 'react'
import type { CSSProperties } from 'react'
import { ChevronDown } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * Native select component styled to match the design system.
 *
 * Uses the native HTML select element for accessibility and mobile UX.
 * For complex dropdowns with icons or custom rendering, use the
 * DropdownMenu component instead.
 */

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  options: SelectOption[]
  placeholder?: string
  onChange?: (value: string) => void
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, options, placeholder, value, onChange, style, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e.target.value)
    }

    return (
      <div style={styles.wrapper}>
        <select
          ref={ref}
          value={value}
          onChange={handleChange}
          style={{ ...styles.select, ...style }}
          className={cn('input', className)}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option
              key={option.value}
              value={option.value}
              disabled={option.disabled}
            >
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown style={styles.icon} />
      </div>
    )
  }
)
Select.displayName = 'Select'

/**
 * SelectGroup for grouping related options.
 */
export interface SelectGroupProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  groups: {
    label: string
    options: SelectOption[]
  }[]
  placeholder?: string
  onChange?: (value: string) => void
}

export const SelectGroup = React.forwardRef<HTMLSelectElement, SelectGroupProps>(
  ({ className, groups, placeholder, value, onChange, style, ...props }, ref) => {
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange?.(e.target.value)
    }

    return (
      <div style={styles.wrapper}>
        <select
          ref={ref}
          value={value}
          onChange={handleChange}
          style={{ ...styles.select, ...style }}
          className={cn('input', className)}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {groups.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <ChevronDown style={styles.icon} />
      </div>
    )
  }
)
SelectGroup.displayName = 'SelectGroup'

const styles: Record<string, CSSProperties> = {
  wrapper: {
    position: 'relative',
  },
  select: {
    display: 'flex',
    height: '36px',
    width: '100%',
    appearance: 'none',
    alignItems: 'center',
    borderRadius: 'var(--radius-sm)',
    border: '1px solid var(--border-default)',
    background: 'var(--bg-secondary)',
    padding: '4px 32px 4px 12px',
    fontSize: '14px',
    color: 'var(--text-primary)',
    boxShadow: 'var(--shadow-sm)',
    transition: 'all var(--transition-fast)',
    cursor: 'pointer',
    outline: 'none',
  },
  icon: {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '16px',
    height: '16px',
    color: 'var(--text-secondary)',
    pointerEvents: 'none',
  },
}
